import { atom } from "jotai";

/**
 * Licensing-earnings dashboard. Mirrors
 * `order-handler/src/domain/use_cases/v2/analytics/artist_dashboard_use_case.types.ts`.
 *
 * SCOPE: licensing income ONLY — money owed to a design OWNER because OTHER
 * brands used its designs. A brand's own retail sales of its own designs
 * produce no ledger row at all, so nothing here may ever be added to or compared
 * against storefront revenue. All money is integer CENTS.
 */

export type ArtistIncomeGranularity = 'daily' | 'weekly' | 'monthly';

export enum ArtistPayoutSource {
    PERCENTAGE = 'PERCENTAGE',
    SUBSCRIPTION = 'SUBSCRIPTION'
}

export enum ArtistPayoutBatchStatus {
    PENDING = 'PENDING',
    PAID = 'PAID',
    FAILED = 'FAILED'
}

export type LicensingModel = string;
export type LicensingInterval = string;

export interface IDesignLicensee {
    brand_id: number;
    brand_name: string | null;
    status: string;
    licensing_model: LicensingModel;
    licensing_percentage_value: number | null;
    licensing_flat_amount: number | null;
    licensing_flat_interval: LicensingInterval | null;
    licensing_currency: string;
    added_at: string | null;
}

export interface ILicensedDesignUsage {
    design_id: number;
    design_name: string | null;
    licensee_count: number;
    licensees: IDesignLicensee[];
}

/**
 * `clawback_net_cents` is NEGATIVE (a refund reversal) and is already included in
 * `net_cents`. Never Math.abs() it — the net would then read higher than the
 * artist is actually owed.
 */
export interface IIncomeByDesign {
    design_id: number | null;
    design_name: string | null;
    gross_cents: number;
    fee_cents: number;
    net_cents: number;
    percentage_net_cents: number;
    subscription_net_cents: number;
    clawback_net_cents: number;
    currency: string;
}

export interface IIncomeBySourceEntry {
    source: ArtistPayoutSource;
    earning_gross_cents: number;
    earning_fee_cents: number;
    earning_net_cents: number;
    clawback_net_cents: number;
    net_cents: number;
}

export interface IIncomeBySource {
    entries: IIncomeBySourceEntry[];
    total_gross_cents: number;
    total_fee_cents: number;
    total_net_cents: number;
    currency: string;
}

export interface IArtistPayoutBatch {
    id?: number;
    created_at?: string;
    brand_id: number;
    period_start: string;
    period_end: string;
    /** MAY BE NEGATIVE when clawbacks exceed earnings; no transfer is issued then. */
    total_net_cents: number;
    currency: string;
    stripe_transfer_id?: string | null;
    status: ArtistPayoutBatchStatus;
}

export interface IArtistPayoutStatusSummary {
    /** Accrued, awaiting the next monthly batch. */
    pending_cents: number;
    /** Parked because Connect payouts were unavailable at batch time. */
    held_cents: number;
    paid_cents: number;
    currency: string;
    connect_onboarding_complete: boolean;
    recent_batches: IArtistPayoutBatch[];
}

export interface IArtistIncomePoint {
    date: string;
    percentage_net_cents: number;
    subscription_net_cents: number;
    net_cents: number;
}

export interface IArtistDashboardResponse {
    designsInUse: ILicensedDesignUsage[];
    incomeByDesign: IIncomeByDesign[];
    incomeBySource: IIncomeBySource;
    payoutStatus: IArtistPayoutStatusSummary;
    timeSeries: IArtistIncomePoint[];
    granularity: ArtistIncomeGranularity;
}

export interface IArtistDashboardRange {
    startDate: string | null;
    endDate: string | null;
    presetId: string;
}

export const artistDashboardPayloadAtom = atom<IArtistDashboardResponse | null>(null);
export const artistDashboardLoadingAtom = atom<boolean>(false);
export const artistDashboardRangeAtom = atom<IArtistDashboardRange>({
    startDate: null,
    endDate: null,
    presetId: '90d'
});
